import { KeyboardEvent, FocusEvent, useState, useEffect, useRef } from 'react';

import { DataSeriesEntryForecast } from '@/modules/calendar/model/calendar/data-series-entry-forecast';
import { DataSeriesEntry } from '@/modules/calendar/model/calendar/data-series-entry';
import styles from '@/modules/calendar/components/forecast-editor/index.module.scss';
import { userForecastService } from '@/modules/calendar/services';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { SessionIdentity } from '@/core/auth/export';
import forecastGameService from '@/modules/calendar/services/forecast-game-service';

export default function ForecastEditor({
  event,
  entry,
  date,
  onChange,
  identity,
}: {
  event: NewsEvent;
  entry: DataSeriesEntry;
  date: string;
  onChange: (forecast?: DataSeriesEntryForecast) => void;
  identity: SessionIdentity;
}) {
  const [forecast, setForecast] = useState<DataSeriesEntryForecast | undefined>(entry.forecast());
  const [editing, setEditing] = useState<boolean>(false);

  const { sessionId, subject } = identity;
  // Enter's programmatic blur() below fires the real onBlur handler; this
  // guards against submitting the same value twice for one user action.
  const suppressNextBlur = useRef(false);

  useEffect(() => {
    setForecast(entry.forecast());
    setEditing(false);
  }, [entry, date]);

  function setUserValue(value: string | null): boolean {
    value = value?.trim() || null;

    if (value === null || value === '' || isNaN(parseFloat(value))) {
      removeUserForecast();
      return false;
    }

    value = `${parseFloat(value)}`;

    const entryForecast = entry.addForecast({
      source: 'USER',
      value,
    });

    setForecast(entryForecast);

    userForecastService
      .setForecastForSeriesEntry(date, entry.dataSeriesId, entryForecast)
      .then(() => {
        onChange(entryForecast);
      });

    forecastGameService.recordForecast(
      {
        dataSeriesId: entry.dataSeriesId,
        seriesDisplay: entry.display,
        date,
        dataReleaseId: event.dataReleaseId,
        userValue: entryForecast.value,
        mniValue: entry.forecasts.find(f => f.source === 'MNI')?.value,
      },
      !!entry.actual,
    );

    return true;
  }

  const onKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = (event.target as HTMLFormElement).value;

      suppressNextBlur.current = true;
      submitValue(value);
      (event.target as HTMLElement).blur();

      setEditing(false);
    }
  };

  const removeUserForecast = () => {
    console.log('Removing user forecast');

    entry.removeUserForecast();

    setForecast(entry.forecast());

    userForecastService.removeForecastForSeriesEntry(date, entry.dataSeriesId).then(() => {
      onChange();

      setEditing(false);
    });

    forecastGameService.removeForecast(entry.dataSeriesId, date);
  };

  function submitValue(value: string): void {
    setUserValue(value);

    analyticsService.recordAnalyticsEvent(AnalyticsEvent.HSC_FORECAST_EDITED, {
      subject,
      object: {
        type: AnalyticsTypes.EVENT,
        eventSubType: AnalyticsSubTypeEvent.DATA_EVENT,
        id: event.id,
        title: `[${event.country}] ${event.title}`,
        config: {
          date: event.date,
          dataReleaseId: event.dataReleaseId,
          dataSeriesIds: event.dataSeriesEntries.map(ds => ds.dataSeriesId),
          instantAnswers: event.instantAnswers?.map(ia => ({
            instantAnswersSeriesId: ia.instantAnswersSeriesId,
            instantAnswersInstanceId: ia.instantAnswersInstanceId,
            questionIds: ia.questions.map(q => q.questionId),
          })),
          forecast: {
            dataSeriesId: entry.dataSeriesId,
            title: entry.display,
            value: value,
          },
        },
      },
      session: {
        id: sessionId,
      },
    });
  }

  const onBlur = (focusEvent: FocusEvent<HTMLElement>) => {
    if (suppressNextBlur.current) {
      suppressNextBlur.current = false;
      return;
    }

    const value = (focusEvent.target as HTMLFormElement).value;

    submitValue(value);

    setEditing(false);
  };

  return (
    <>
      {editing || (
        <small className={`text-text-dark`} onClick={() => setEditing(true)}>
          {forecast?.value || <span className={styles.emptyForecast} />}
        </small>
      )}

      {editing && (
        <input
          autoFocus={true}
          type={'text'}
          step={'0.001'}
          size={3}
          defaultValue={forecast?.value}
          onBlur={event => onBlur(event)}
          onKeyDown={event => onKey(event)}
          className={styles.forecastEditor}
        />
      )}
      {forecast?.source === 'USER' && (
        <span
          className={` ms-1 ${styles.removeForecastButton}`}
          onClick={() => removeUserForecast()}
        >
          ✕
        </span>
      )}

      <div
        className={forecast?.source === 'USER' ? 'text-success' : 'text-secondary'}
        style={{ fontSize: '0.6em' }}
      >
        {/* eslint-disable-next-line  */}
        {forecast?.source == 'MNI' ? '' : forecast?.source}
      </div>
    </>
  );
}
